import Breadcrumb from 'Components/UI/Breadcrumb'
import React, { useEffect, useState } from 'react'
import { Button, Card, CardBody, CardTitle, Col, Container, Form, Input, Label, Row, FormFeedback } from "reactstrap";
import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate, useParams } from 'react-router-dom';
import { getHoliday, updateHoliday } from 'helpers/API Calls/System & Settings/Holidays';
import Select from "react-select";
import { BOOKING_ACTION_OPTIONS } from './AddHoliday';
import Spinner from 'Components/UI/Spinner';

function EditHoliday() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { id } = useParams();
  interface FormValues {
    name: string;
    fromDate: string;
    toDate: string;
    message: string;
    bookingAction: { label: string; value: string } | null;
  }

  const formik = useFormik<FormValues>({
    initialValues: {
      name: '',
      fromDate: '',
      toDate: '',
      message: '',
      bookingAction: null
    },    validationSchema: yup.object().shape({
      name: yup.string().required('Please enter holiday name'),
      fromDate: yup.date()
        .min(new Date().toISOString().split('T')[0], 'Start date cannot be in the past')
        .required('Please select start date'),
      toDate: yup.date()
        .min(yup.ref('fromDate'), 'End date must be greater than or equal to start date')
        .required('Please select end date'),
      message: yup.string().when('bookingAction', {
        is: (bookingAction: FormValues['bookingAction']) => bookingAction?.value === 'display_warning',
        then: (schema) => schema.required('Message is required when warning is displayed'),
        otherwise: (schema) => schema.notRequired(),
      }),
      bookingAction: yup.object().shape({
        label: yup.string().required(), 
        value: yup.string().required()
      }).nullable().required('Please select booking action'),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);      
      const data = {
        title: values.name,
        from_date: values.fromDate,
        to_date: values.toDate,
        message: values.message,
        action: values.bookingAction?.value || '',
      };
      
      const response = await updateHoliday(Number(id), data);
      setSubmitting(false);
      if (response.success) {
        navigate('/holidays');
      }
    },
  });

  useEffect(() => {
    const fetchCategory = async () => {
      if (id) {
        const response = await getHoliday(Number(id));
        if (response.success) {
          const bookingActionLabel = BOOKING_ACTION_OPTIONS.find(option => option.value === response.data.action)?.label || '';
          formik.setValues({
            name: response.data.title || '',
            fromDate: response.data.from_date || '',
            toDate: response.data.to_date || '',
            message: response.data.message || '',
            bookingAction: { label: bookingActionLabel, value: response.data.action },
          });
        }
      }
      setIsLoading(false);
    };
    fetchCategory();
  }, [id]);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb items={[{ label: "Holidays Settings", link: "/holidays" }, { label: "Edit" }]} />
        {isLoading ? <div className="position-relative vh-100"><Spinner/></div>
        :
        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit} autoComplete="off">
            <CardTitle tag="h4" className="mb-4">Holiday Details</CardTitle>
              <Row>
                <Col md="6">
                  <div className="mb-3">
                    <Label htmlFor="name">Holiday Name <span className="text-danger">*</span></Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Enter holiday name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      invalid={formik.touched.name && formik.errors.name ? true : false}
                    />
                    {formik.errors.name && formik.touched.name ? (
                      <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
                    ) : null}
                  </div>

                   <div className="mb-3">
                    <Label htmlFor="bookingAction">Frontend Bookings <span className="text-danger">*</span></Label>
                    <Select
                      id="bookingAction"
                      name="bookingAction"
                      options={BOOKING_ACTION_OPTIONS}
                      value={formik.values.bookingAction}
                      onChange={(value) => formik.setFieldValue("bookingAction", value)}
                      onBlur={formik.handleBlur}
                      placeholder="Select action for frontend bookings"
                      className={
                        formik.touched.bookingAction && formik.errors.bookingAction
                          ? "is-invalid"
                          : ""
                      }
                    />
                    {formik.touched.bookingAction && formik.errors.bookingAction ? (
                      <div className="invalid-feedback d-block">
                        {formik.errors.bookingAction}
                      </div>
                    ) : null}
                  </div>
                </Col>
                <Col md="6">
                
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label htmlFor="fromDate">From Date <span className="text-danger">*</span></Label>                        <Input
                          id="fromDate"
                          name="fromDate"
                          type="date"
                          min={new Date().toISOString().split('T')[0]}
                          value={formik.values.fromDate}
                          onChange={formik.handleChange}
                          invalid={formik.touched.fromDate && formik.errors.fromDate ? true : false}
                        />
                        {formik.errors.fromDate && formik.touched.fromDate ? (
                          <FormFeedback type="invalid">{formik.errors.fromDate}</FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label htmlFor="toDate">To Date <span className="text-danger">*</span></Label>                        <Input
                          id="toDate"
                          name="toDate"
                          type="date"
                          min={formik.values.fromDate || new Date().toISOString().split('T')[0]}
                          value={formik.values.toDate}
                          onChange={formik.handleChange}
                          invalid={formik.touched.toDate && formik.errors.toDate ? true : false}
                        />
                        {formik.errors.toDate && formik.touched.toDate ? (
                          <FormFeedback type="invalid">{formik.errors.toDate}</FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                  </Row>

                  {formik.values.bookingAction?.value === 'display_warning' && <div className="mb-3">
                    <Label htmlFor="message">Message To Display On Booking Form</Label>
                    <Input
                      id="message"
                      name="message"
                      type="textarea"
                      rows={4}
                      placeholder="Enter message to display to customers"
                      value={formik.values.message}
                      onChange={formik.handleChange}
                      invalid={formik.touched.message && formik.errors.message ? true : false}
                    />
                    {formik.errors.message && formik.touched.message ? (
                      <FormFeedback type="invalid">{formik.errors.message}</FormFeedback>
                    ) : null}
                  </div>}

                
                </Col>
              </Row>

              <div className="d-flex flex-wrap gap-2 mt-3 justify-content-end">
                <Button type="submit" color="primary" disabled={formik.isSubmitting}>
                  {formik.isSubmitting ? 'Updating...' : 'Update Holiday'}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
        }
      </Container>
    </div>
  );
}

export default EditHoliday
